// Chakra imports
import {
  Box,
  Text,
  useColorModeValue,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  Divider,
  Icon,
  Button,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import React, { useState, useEffect, useRef } from "react";
import { MdAttachMoney, MdAdd, MdDelete, MdEdit } from "react-icons/md";
import { formatDate, formatCurrency } from "utils/format";
import amountsService from "services/amountsService";

export default function AmountsManagement() {
  const toast = useToast();
  const cancelRef = useRef();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.100");
  const iconBoxBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const emptyIconBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const inputBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amounts, setAmounts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({ value: "", name: "" });
  const [formErrors, setFormErrors] = useState({});

  const fetchAmounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await amountsService.getAmounts();
      if (result.success && result.data) setAmounts(result.data);
      else setError(result.error || "Failed to load amounts");
    } catch (err) {
      console.error(err);
      setError("Failed to load amounts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAmounts(); }, []);

  const handleOpenCreate = () => {
    setFormData({ value: "", name: "" });
    setFormErrors({});
    setEditTarget(null);
    onCreateOpen();
  };

  const handleOpenEdit = (amount) => {
    setEditTarget(amount);
    setFormData({ value: String(amount.value ?? ""), name: amount.name ?? "" });
    setFormErrors({});
    onEditOpen();
  };

  const handleCreate = async () => {
    const errors = {};
    const val = formData.value?.trim();
    if (!val) errors.value = "Amount is required";
    else if (isNaN(Number(val)) || Number(val) < 0) errors.value = "Enter a valid number ≥ 0";
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setCreating(true);
    setFormErrors({});
    try {
      const result = await amountsService.createAmount({ value: Number(val), name: formData.name?.trim() });
      if (result.success) {
        toast({ title: "Success", description: result.message, status: "success", duration: 5000, isClosable: true });
        onCreateClose();
        fetchAmounts();
      } else {
        toast({ title: "Error", description: result.error, status: "error", duration: 5000, isClosable: true });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to create amount.", status: "error", duration: 5000, isClosable: true });
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async () => {
    if (!editTarget) return;
    const errors = {};
    const val = formData.value?.trim();
    if (!val) errors.value = "Amount is required";
    else if (isNaN(Number(val)) || Number(val) < 0) errors.value = "Enter a valid number ≥ 0";
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setUpdating(true);
    setFormErrors({});
    try {
      const result = await amountsService.updateAmount(editTarget.id, { value: Number(val), name: formData.name?.trim() });
      if (result.success) {
        toast({ title: "Success", description: result.message, status: "success", duration: 5000, isClosable: true });
        onEditClose();
        setEditTarget(null);
        fetchAmounts();
      } else {
        toast({ title: "Error", description: result.error, status: "error", duration: 5000, isClosable: true });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to update amount.", status: "error", duration: 5000, isClosable: true });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteClick = (amount) => {
    setDeleteTarget(amount);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await amountsService.deleteAmount(deleteTarget.id);
      if (result.success) {
        toast({ title: "Success", description: result.message, status: "success", duration: 5000, isClosable: true });
        onDeleteClose();
        setDeleteTarget(null);
        fetchAmounts();
      } else {
        toast({ title: "Error", description: result.error, status: "error", duration: 5000, isClosable: true });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to delete amount.", status: "error", duration: 5000, isClosable: true });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Flex justify="center" align="center" minH="400px"><Spinner size="xl" color="brand.500" /></Flex>
      </Box>
    );
  }
  if (error) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Alert status="error" borderRadius="15px"><AlertIcon /><Text color={textColor}>{error}</Text></Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} maxW="1000px" mx="auto">
      <Flex direction="column" gap="6">
        <Flex align="center" justify="space-between" flexWrap="wrap" gap="4">
          <Flex align="center" gap="3">
            <Flex w="12" h="12" borderRadius="xl" bg={iconBoxBg} align="center" justify="center">
              <Icon as={MdAttachMoney} color="brand.500" boxSize="6" />
            </Flex>
            <Box>
              <Text color={textColor} fontSize="2xl" fontWeight="700">Amounts</Text>
              <Text color={textColorSecondary} fontSize="sm">Phase fees used by centers. Create amounts, then assign them to center phases.</Text>
            </Box>
          </Flex>
          <Button leftIcon={<Icon as={MdAdd} />} colorScheme="brand" size="md" borderRadius="lg" onClick={handleOpenCreate}>Add Amount</Button>
        </Flex>

        {amounts.length > 0 ? (
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing="5">
            {amounts.map((a) => (
              <Card key={a.id} p="5" bg={cardBg} borderRadius="16px" boxShadow="sm" borderWidth="1px" borderColor={borderColor} _hover={{ shadow: "md", borderColor: "brand.200" }}>
                <Flex direction="column" gap="3">
                  <Flex w="12" h="12" borderRadius="xl" bg={iconBoxBg} align="center" justify="center">
                    <Icon as={MdAttachMoney} color="brand.500" boxSize="6" />
                  </Flex>
                  <Text color={textColor} fontSize="xl" fontWeight="700">{formatCurrency(a.value)}</Text>
                  {a.name && <Text color={textColorSecondary} fontSize="sm">{a.name}</Text>}
                  <Divider borderColor={borderColor} />
                  <Flex justify="space-between" align="center" flexWrap="wrap" gap="2">
                    <Text color={textColorSecondary} fontSize="xs">Added {a.created_at ? formatDate(a.created_at) : "—"}</Text>
                    <Flex gap="1">
                      <Button size="xs" leftIcon={<Icon as={MdEdit} />} colorScheme="brand" variant="ghost" onClick={() => handleOpenEdit(a)}>Edit</Button>
                      <Button size="xs" leftIcon={<Icon as={MdDelete} />} colorScheme="red" variant="ghost" onClick={() => handleDeleteClick(a)}>Delete</Button>
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            ))}
          </SimpleGrid>
        ) : (
          <Card p="0" bg={cardBg} borderRadius="16px" boxShadow="sm">
            <Flex direction="column" align="center" justify="center" py="16" px="6">
              <Flex w="16" h="16" borderRadius="full" bg={emptyIconBg} align="center" justify="center" mb="4">
                <Icon as={MdAttachMoney} color={textColorSecondary} boxSize="8" />
              </Flex>
              <Text color={textColor} fontWeight="600" mb="1">No amounts yet</Text>
              <Text color={textColorSecondary} fontSize="sm" textAlign="center" mb="4">Create amounts (e.g. registration fee, phase fees), then assign them to centers.</Text>
              <Button leftIcon={<Icon as={MdAdd} />} colorScheme="brand" size="sm" onClick={handleOpenCreate}>Add Amount</Button>
            </Flex>
          </Card>
        )}
      </Flex>

      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>Add Amount</ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody pb="6">
            <FormControl isRequired isInvalid={!!formErrors.value} mb="4">
              <FormLabel color={textColor}>Value (XAF)</FormLabel>
              <Input type="number" min={0} value={formData.value} onChange={(e) => { setFormData({ ...formData, value: e.target.value }); if (formErrors.value) setFormErrors({ ...formErrors, value: null }); }} placeholder="e.g. 50000" bg={inputBg} color={textColor} />
              {formErrors.value && <Text color="red.500" fontSize="sm" mt="1">{formErrors.value}</Text>}
            </FormControl>
            <FormControl>
              <FormLabel color={textColor}>Name (optional)</FormLabel>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Registration fee" bg={inputBg} color={textColor} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleCreate} isLoading={creating} loadingText="Creating">Create Amount</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isEditOpen} onClose={onEditClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>Edit Amount</ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody pb="6">
            <FormControl isRequired isInvalid={!!formErrors.value} mb="4">
              <FormLabel color={textColor}>Value (XAF)</FormLabel>
              <Input type="number" min={0} value={formData.value} onChange={(e) => { setFormData({ ...formData, value: e.target.value }); if (formErrors.value) setFormErrors({ ...formErrors, value: null }); }} placeholder="e.g. 50000" bg={inputBg} color={textColor} />
              {formErrors.value && <Text color="red.500" fontSize="sm" mt="1">{formErrors.value}</Text>}
            </FormControl>
            <FormControl>
              <FormLabel color={textColor}>Name (optional)</FormLabel>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Registration fee" bg={inputBg} color={textColor} />
            </FormControl>
            {editTarget && (
              <Text color={textColorSecondary} fontSize="xs" mt="2">
                Updating the value will update all student tuitions that use this amount (via their center).
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleUpdate} isLoading={updating} loadingText="Updating">Update Amount</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader color={textColor}>Delete Amount</AlertDialogHeader>
            <AlertDialogBody>
              Delete this amount? If it is assigned to any center phase, the request will fail. You must remove it from all centers first.
              {deleteTarget && <Text color={textColorSecondary} fontSize="sm" mt="2">{formatCurrency(deleteTarget.value)} {deleteTarget.name && `– ${deleteTarget.name}`}</Text>}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>Cancel</Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3} isLoading={deleting} loadingText="Deleting">Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
